import * as S from '@effect/schema/Schema'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'

import type { CurriculumApi } from '~/@entities/Curriculum/api'
import { NON_STANDARD_SUBJECT_HIGH_KO, SUBJECT_HIGH_KO } from '~/@entities/Curriculum/constant'

import type { SCHOOL_TYPE_KO_ABBR } from './constants'
import { SCHOOL_TYPE } from './constants'

/**
 * CONSTANTS FOR SCHEMA
 * 오직 schema를 위해서 정의된 값. api타입에 의존하기 위해 만듦. ex)CurriculumApi.Grade[]
 * 스키마가 사라지면 같이 사라져야 함. 만약 이 파일(schema) 외부에서 쓴다면 const 쪽으로 분리
 */
const _ELEMENTARY_GRADE: CurriculumApi.Grade[] = [1, 2, 3, 4, 5, 6] as const
const ELEMENTARY_GRADE_STRING = _ELEMENTARY_GRADE.map(String) as [
  `${(typeof _ELEMENTARY_GRADE)[number]}`,
]
const _GRADE_MIDDLE: Extract<CurriculumApi.Grade, 1 | 2 | 3>[] = [1, 2, 3] as const
const GRADE_MIDDLE_STRING = _GRADE_MIDDLE.map(String) as [`${(typeof _GRADE_MIDDLE)[number]}`]

const CURRICULUM_SEMESTER = [1, 2] as const
const CURRICULUM_SEMESTER_HIGH = null

/***************** 학교 *****************/
/**
 * 학년 타입 (초, 중, 고)
 * ELEMENTARY: 초등학교
 * MIDDLE: 중학교
 * HIGH: 고등학교
 */
export const schoolType = S.enums(SCHOOL_TYPE)
export type SchoolType = CurriculumApi.SchoolType
/******************************************/

/***************** 학년 *****************/
// 초등학교 학년
const gradeElementary = S.literal(...ELEMENTARY_GRADE_STRING)
// 중학교 학년
const gradeMiddle = S.literal(...GRADE_MIDDLE_STRING)
// 고등학교 학년 = 고등학생 과목: 고등학생은 학년이 없고 과목만 있다. 다른 SchoolType의 Grade에 해당
const gradeHigh = S.enums({ ...SUBJECT_HIGH_KO, ...NON_STANDARD_SUBJECT_HIGH_KO })
// 초중
export const gradeElemMiddle = S.union(gradeElementary, gradeMiddle)
// 전체 학년(초, 중, 고) <br/> 고등학교는 학년이 없고 과목만 있어서, 1~6학년의 값도 다 string으로
export const allGradeSubject = S.union(gradeElemMiddle, gradeHigh)

export type NumberGradeElementary = CurriculumApi.Grade
export type NumberGradeMiddle = Extract<CurriculumApi.Grade, 1 | 2 | 3>
export type GradeElementary = `${CurriculumApi.Grade}` //S.Schema.To<typeof GradeElementary> api에 타입이 있는데 스키마에서 또 타입으로 변환할 필요가 없어서 주석....
export type GradeMiddle = `${Extract<CurriculumApi.Grade, 1 | 2 | 3>}` // S.Schema.To<typeof GradeMiddle>
export type GradeHigh = CurriculumApi.HighSchoolSubject // S.Schema.To<typeof GradeHigh>

export type AllGradeSubject = GradeElementary | CurriculumApi.HighSchoolSubject // S.Schema.To<typeof AllGradeSubject>
export type GradeElementaryMiddle = GradeElementary | GradeMiddle
/******************************************/

/***************** 학기 *****************/
/**
 * 고등학교의 경우 학기가 없고 과목만 있어서, 고등학교 이하만 semester 값을 갖는다.
 * 그럼에도 학년과 학기를 정의해야하는 경우에 3학기로 정한다.
 * 예외 케이스)
 * 기하: 고등학교 3학년 3학기
 * (구)교육과정: 고등학교 4학년 1학기
 *
 * 25.8.27 추가 주석
 * semester에는 두가지 개념이 공존한다
 * (1) 커리큘럼 관점에서의 semester
 * (2) 데이터 관점에서의 semester - conceptSemester
 * 실제로 런타임에서 이 값이 구분되어있지만, 타입적으로 하나의 semester로 바라보고 있다.
 * conceptSemester 2번, semester를 curriculumSemester로 간주한다.
 * BE에서 conceptSemester는 무용한 값이라고해서 주석처리 해놓음
 */

export const semester = S.union(
  S.literal(...CURRICULUM_SEMESTER),
  S.literal(CURRICULUM_SEMESTER_HIGH),
)
// export const conceptSemester = S.union(S.literal(...CURRICULUM_SEMESTER), S.literal(3))
export type Semester = S.Schema.To<typeof semester>
// export type ConceptSemester = S.Schema.To<typeof conceptSemester>

/***************** 학교 학년 *****************/
export type GradeElementaryMiddleAbbr =
  `${ValueOf<typeof SCHOOL_TYPE_KO_ABBR>}${GradeElementaryMiddle}`

/***************** 개정 *****************/
export type Revision = CurriculumApi.Revision
